import {
  PhaseStructureTypeEnum,
  SubdivisionOverrideTypeEnum,
} from '@vatos-pas/common';
import {
  optionalPercentageErrors,
  requiredPercentageErrors,
  requiredPositivePercentageErrors,
} from 'utils';
import { SubDivisionFormValues } from '../containers/useSubDivisionContainer';

const ZIP_CODE_REGEX = new RegExp(/^\d{5}(-\d{4})?$/);

type ValidationErrors = Record<string, string[] | undefined>;

export const validateSubdivisionCreation = async (
  values: SubDivisionFormValues,
): Promise<ValidationErrors> => {
  const errors: ValidationErrors = {};

  if (!values.name) {
    errors.name = ['The name is required'];
  }
  if (!values.address1) {
    errors.address1 = ['The Address is required'];
  }
  if (!values.warrantyStatus) {
    errors.warrantyStatus = ['The Warranty Status is required'];
  }
  if (!values.city) {
    errors.city = ['The city is required'];
  }
  if (!values.zip) {
    errors.zip = ['The zip is required'];
  } else if (!values.zip.match(ZIP_CODE_REGEX)) {
    errors.zip = [
      'Zip code must be entered either in 5 digit format (12345) or 9 digit format (12345-1234)',
    ];
  }
  if (!values.state) {
    errors.state = ['The state is required'];
  }
  // phaseStructureType is required from values - no localStorage fallback
  const phaseStructureType = values.phaseStructureType;

  if (values.overrideRegionCostPerBoard) {
    if (values.garageCostFixed && values.garageCostFixed < 0) {
      errors.garageCostFixed = ['The Garage (Fixed cost) must be at least 0'];
    }

    if (!values.garageCostFixed && values.garageCostFixed !== 0) {
      errors.garageCostFixed = ['The Garage (Fixed cost) is required'];
    }

    if (values.highCeilingPerBoard && values.highCeilingPerBoard < 0) {
      errors.highCeilingPerBoard = [
        'The High Ceiling (Per Board) must be at least 0',
      ];
    }

    if (!values.highCeilingPerBoard && values.highCeilingPerBoard !== 0) {
      errors.highCeilingPerBoard = ['The High Ceiling (Per Board) is required'];
    }

    if (values.screwInspectionPerBoard && values.screwInspectionPerBoard < 0) {
      errors.screwInspectionPerBoard = [
        'The Screw Inspection (Per Board) must be at least 0',
      ];
    }

    if (
      !values.screwInspectionPerBoard &&
      values.screwInspectionPerBoard !== 0
    ) {
      errors.screwInspectionPerBoard = [
        'The Screw Inspection (Per Board) is required',
      ];
    }

    const pieceworkErrors = requiredPercentageErrors(
      values.pieceworkPercentage,
      'Piecework Percentage',
    );
    if (pieceworkErrors) {
      errors.pieceworkPercentage = pieceworkErrors;
    }

    const specialJobsErrors = requiredPercentageErrors(
      values.specialJobsPercentage,
      'Special Jobs Percentage',
    );
    if (specialJobsErrors) {
      errors.specialJobsPercentage = specialJobsErrors;
    }
    if (phaseStructureType === PhaseStructureTypeEnum.Drywall) {
      if (values.dwHangingCostPerBoard && values.dwHangingCostPerBoard < 0) {
        errors.dwHangingCostPerBoard = [
          'The Hanging (Per board) must be at least 0',
        ];
      }

      if (!values.dwHangingCostPerBoard && values.dwHangingCostPerBoard !== 0) {
        errors.dwHangingCostPerBoard = ['The Hanging (Per board) is required'];
      }

      if (
        values.dwFinishingCostPerBoard &&
        values.dwFinishingCostPerBoard < 0
      ) {
        errors.dwFinishingCostPerBoard = [
          'The Finishing (Per board) must be at least 0',
        ];
      }

      if (
        !values.dwFinishingCostPerBoard &&
        values.dwFinishingCostPerBoard !== 0
      ) {
        errors.dwFinishingCostPerBoard = [
          'The Finishing (Per board) is required',
        ];
      }

      if (values.dwSprayingCostPerBoard && values.dwSprayingCostPerBoard < 0) {
        errors.dwSprayingCostPerBoard = [
          'The Spraying (Per board) must be at least 0',
        ];
      }

      if (
        !values.dwSprayingCostPerBoard &&
        values.dwSprayingCostPerBoard !== 0
      ) {
        errors.dwSprayingCostPerBoard = [
          'The Spraying (Per board) is required',
        ];
      }
    }

    // Drywall Paint Section

    if (phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint) {
      if (values.dwpHangingCostPerBoard && values.dwpHangingCostPerBoard < 0) {
        errors.dwpHangingCostPerBoard = [
          'The Hang (Per board) must be at least 0',
        ];
      }

      if (
        !values.dwpHangingCostPerBoard &&
        values.dwpHangingCostPerBoard !== 0
      ) {
        errors.dwpHangingCostPerBoard = ['The Hang (Per board) is required'];
      }

      if (values.dwpFinishCostPerBoard && values.dwpFinishCostPerBoard < 0) {
        errors.dwpFinishCostPerBoard = [
          'The Finish (Per Board) must be at least 0',
        ];
      }

      if (!values.dwpFinishCostPerBoard && values.dwpFinishCostPerBoard !== 0) {
        errors.dwpFinishCostPerBoard = ['The dwpFinishCostPerBoardis required'];
      }

      if (values.dwpPrimeCostPerSqft && values.dwpPrimeCostPerSqft < 0) {
        errors.dwpPrimeCostPerSqft = [
          'The Prime (Per Sq Ft) must be at least 0',
        ];
      }

      if (!values.dwpPrimeCostPerSqft && values.dwpPrimeCostPerSqft !== 0) {
        errors.dwpPrimeCostPerSqft = ['The Prime (Per Sq Ft) is required'];
      }

      if (values.dwpBump1CostPerBoard && values.dwpBump1CostPerBoard < 0) {
        errors.dwpBump1CostPerBoard = [
          'The Drywall Touch Up 1 (Per board) must be at least 0',
        ];
      }

      if (!values.dwpBump1CostPerBoard && values.dwpBump1CostPerBoard !== 0) {
        errors.dwpBump1CostPerBoard = [
          'The Drywall Touch Up 1 (Per board) is required',
        ];
      }

      if (values.dwpTrim1CostPerSqft && values.dwpTrim1CostPerSqft < 0) {
        errors.dwpTrim1CostPerSqft = [
          'The Trim 1 (Per Sq Ft) must be at least 0',
        ];
      }

      if (!values.dwpTrim1CostPerSqft && values.dwpTrim1CostPerSqft !== 0) {
        errors.dwpTrim1CostPerSqft = ['The Trim 1 (Per Sq Ft) is required'];
      }

      if (
        values.dwpInteriorPaintCostPerSqft &&
        values.dwpInteriorPaintCostPerSqft < 0
      ) {
        errors.dwpInteriorPaintCostPerSqft = [
          'The Interior Paint (Per Sq Ft) must be at least 0',
        ];
      }

      if (
        !values.dwpInteriorPaintCostPerSqft &&
        values.dwpInteriorPaintCostPerSqft !== 0
      ) {
        errors.dwpInteriorPaintCostPerSqft = [
          'The Interior Paint (Per Sq Ft) is required',
        ];
      }

      if (values.dwpBump2CostPerBoard && values.dwpBump2CostPerBoard < 0) {
        errors.dwpBump2CostPerBoard = [
          'The Drywall Touch Up 2 (Per board) must be at least 0',
        ];
      }

      if (!values.dwpBump2CostPerBoard && values.dwpBump2CostPerBoard !== 0) {
        errors.dwpBump2CostPerBoard = [
          'The Drywall Touch Up 2 (Per board) is required',
        ];
      }

      if (values.dwpTrim2CostPerSqft && values.dwpTrim2CostPerSqft < 0) {
        errors.dwpTrim2CostPerSqft = [
          'The Trim 2 (Per Sq Ft) must be at least 0',
        ];
      }

      if (!values.dwpTrim2CostPerSqft && values.dwpTrim2CostPerSqft !== 0) {
        errors.dwpTrim2CostPerSqft = ['The Trim 2 (Per Sq Ft) is required'];
      }

      if (
        values.dwpFinalPaintCostPerSqft &&
        values.dwpFinalPaintCostPerSqft < 0
      ) {
        errors.dwpFinalPaintCostPerSqft = [
          'The Final Paint (Per Sq Ft) must be at least 0',
        ];
      }

      if (
        !values.dwpFinalPaintCostPerSqft &&
        values.dwpFinalPaintCostPerSqft !== 0
      ) {
        errors.dwpFinalPaintCostPerSqft = [
          'The Final Paint (Per Sq Ft) is required',
        ];
      }

      if (values.dwpBump3CostPerBoard && values.dwpBump3CostPerBoard < 0) {
        errors.dwpBump3CostPerBoard = [
          'The Drywall Touch Up 3 (Per board) must be at least 0',
        ];
      }

      if (!values.dwpBump3CostPerBoard && values.dwpBump3CostPerBoard !== 0) {
        errors.dwpBump3CostPerBoard = [
          'The Drywall Touch Up 3 (Per board) is required',
        ];
      }

      if (
        values.dwpCMBluetapeCostPerSqft &&
        values.dwpCMBluetapeCostPerSqft < 0
      ) {
        errors.dwpCMBluetapeCostPerSqft = [
          'The CM Bluetape (Per Sq Ft) must be at least 0',
        ];
      }

      if (
        !values.dwpCMBluetapeCostPerSqft &&
        values.dwpCMBluetapeCostPerSqft !== 0
      ) {
        errors.dwpCMBluetapeCostPerSqft = [
          'The CM Bluetape (Per Sq Ft) is required',
        ];
      }

      if (
        values.dwpFinalBluetapeCostPerSqft &&
        values.dwpFinalBluetapeCostPerSqft < 0
      ) {
        errors.dwpFinalBluetapeCostPerSqft = [
          'The Final Bluetape (Per Sq Ft) must be at least 0',
        ];
      }

      if (
        !values.dwpFinalBluetapeCostPerSqft &&
        values.dwpFinalBluetapeCostPerSqft !== 0
      ) {
        errors.dwpFinalBluetapeCostPerSqft = [
          'The Final Bluetape (Per Sq Ft) is required',
        ];
      }

      const optionPercentageErrors =
        values.overrideType === SubdivisionOverrideTypeEnum.FIXED
          ? requiredPositivePercentageErrors
          : optionalPercentageErrors;

      const interiorOptionsErrors = optionPercentageErrors(
        values.interiorOptionsPercentage,
        'Paint Interior Options',
      );
      if (interiorOptionsErrors) {
        errors.interiorOptionsPercentage = interiorOptionsErrors;
      }

      const exteriorOptionsErrors = optionPercentageErrors(
        values.exteriorOptionsPercentage,
        'Paint Exterior Options',
      );
      if (exteriorOptionsErrors) {
        errors.exteriorOptionsPercentage = exteriorOptionsErrors;
      }

      const trimOptionErrors = optionPercentageErrors(
        values.trimOptionPercentage,
        'Trim Option',
      );
      if (trimOptionErrors) {
        errors.trimOptionPercentage = trimOptionErrors;
      }
    }
  }

  if (!values.builderId) {
    errors.builderId = ['The Builder is required'];
  }

  if (!values.regionId) {
    errors.regionId = ['The Region is required'];
  }

  if (values.geoDistanceTolerance === undefined || values.geoDistanceTolerance === null) {
    errors.geoDistanceTolerance = ['The Default Geofencing Threshold is required'];
  } else if (
    !Number.isInteger(values.geoDistanceTolerance) ||
    values.geoDistanceTolerance < 100 ||
    values.geoDistanceTolerance > 10000
  ) {
    errors.geoDistanceTolerance = ['Value must be greater than 100 and lower than 10,000 ft'];
  }

  return errors;
};
